/* eslint-disable */
"use client";
import React, { useEffect, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { getAuthStatus } from "@/lib/auth";
import { SectionTitle } from "@/components/common/SectionTitle";
import { KeywordLabel } from "@/components/common/KeywordLabel";
import BottomToast from "@/components/common/BottomToast";
import { reportService } from "@/services/report.service";
import type { ReportItem as ApiReportItem } from "@/types/report";

// 카테고리 버튼 컴포넌트
function CategoryButtons({ 
  categories, 
  selectedCategory, 
  onCategoryChange, 
  loading 
}: { 
  categories: Array<{ code: string; name: string }>;
  selectedCategory: string;
  onCategoryChange: (categoryCode: string) => void;
  loading: boolean;
}) {
  if (loading) {
    return (
      <div className="flex justify-start items-center gap-2 lg:gap-2.5 overflow-x-auto pb-2">
        <div className="px-3 lg:px-5 py-2 lg:py-3 rounded-lg bg-neutral-100 animate-pulse flex-shrink-0">
          <div className="w-8 lg:w-12 h-4 lg:h-6 bg-gray-300 rounded"></div>
        </div>
        <div className="px-3 lg:px-5 py-2 lg:py-3 rounded-lg bg-neutral-100 animate-pulse flex-shrink-0">
          <div className="w-12 lg:w-16 h-4 lg:h-6 bg-gray-300 rounded"></div>
        </div>
        <div className="px-3 lg:px-5 py-2 lg:py-3 rounded-lg bg-neutral-100 animate-pulse flex-shrink-0">
          <div className="w-16 lg:w-20 h-4 lg:h-6 bg-gray-300 rounded"></div>
        </div>
      </div>
    );
  }

  // API에서 가져온 카테고리 그대로 사용 (이미 '전체'가 포함되어 있음)
  const allCategories = categories;

  return (
    <div className="flex justify-start items-center gap-2 lg:gap-2.5 overflow-x-auto pb-2">
      {allCategories.map((category) => (
        <button
          key={category.code}
          onClick={() => onCategoryChange(category.code)}
          className={`px-3 lg:px-5 py-2 lg:py-3 rounded-lg flex justify-start items-center gap-1 lg:gap-2.5 transition-colors duration-200 flex-shrink-0 whitespace-nowrap ${
            selectedCategory === category.code
              ? "bg-black text-white text-sm lg:text-lg font-medium font-['Pretendard']"
              : "bg-neutral-100 text-zinc-400 text-sm lg:text-lg font-normal font-['Pretendard'] hover:bg-neutral-200"
          }`}
        >
          {category.name}
        </button>
      ))}
    </div>
  );
}

interface ReportItem { id: number; title: string; summary?: string; createdAt?: string; keywords?: string[]; thumbnailUrl?: string | null }

function SmallReportCard({ item, isMobile }: { item: ReportItem; isMobile?: boolean }) {
  // 로그인 게이트: /auth/me 결과 캐시
  const [isAuthed, setIsAuthed] = useState<boolean | null>((globalThis as any).__dw_latest_auth__ ?? null);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if ((globalThis as any).__dw_latest_auth__ !== undefined && (globalThis as any).__dw_latest_auth__ !== null) {
      setIsAuthed((globalThis as any).__dw_latest_auth__);
      return;
    }
    let mounted = true;
    getAuthStatus()
      .then(d => {
        if (!mounted) return;
        const ok = !!(d?.ok && d?.user);
        (globalThis as any).__dw_latest_auth__ = ok;
        setIsAuthed(ok);
      })
      .catch(() => { if (mounted) { (globalThis as any).__dw_latest_auth__ = false; setIsAuthed(false); } });
    return () => { mounted = false; };
  }, []);

  const [toastVisible, setToastVisible] = useState(false);
  const showLoginToast = () => setToastVisible(true);

  const onBlockedClick: React.MouseEventHandler = (e) => {
    if (isAuthed === false) {
      e.preventDefault();
      showLoginToast();
    } else {
      e.preventDefault();
    }
  };

  return (
    <div className="w-full flex flex-col gap-4 h-auto items-start justify-start relative shrink-0">
      <a
        href={isAuthed === true ? `/reports/${item.id}` : '#'}
        onClick={isAuthed === true ? undefined : onBlockedClick}
        aria-label={item.title || '리포트 상세 보기'}
        className="block w-full h-[200px] lg:h-[230px]"
      >
        <div className="bg-center bg-cover bg-no-repeat h-[200px] lg:h-[230px] rounded-[20px] shrink-0 w-full" style={{ backgroundImage: `url('${item.thumbnailUrl || '/images/ef1c9ea75bde321990b4893a8c6e39e943b9418c.png'}')` }} />
      </a>
      <div className={`box-border w-full flex flex-col gap-3 items-start justify-start px-1 ${!isMobile ? 'pb-6' : 'pb-0'} pt-0 relative shrink-0`}>
        <div className="w-full flex gap-2 h-[27px] items-start justify-start relative shrink-0">
          {(item.keywords?.slice(0,3) || []).map((k,i)=>(<KeywordLabel key={i} text={k} />))}
        </div>
        <div className="w-full flex flex-col gap-4 h-auto items-center justify-start relative shrink-0">
          <div className="w-full flex flex-col font-['Pretendard:Medium',_sans-serif] gap-3 items-start justify-start leading-[0] not-italic relative shrink-0">
            <a
              href={isAuthed === true ? `/reports/${item.id}` : '#'}
              onClick={isAuthed === true ? undefined : onBlockedClick}
              className={`h-auto relative shrink-0 text-[#010103] text-2xl lg:text-[24px] w-full ${isAuthed === true ? 'hover:underline' : ''}`}
            >
              <p className="leading-[1.4] line-clamp-2 font-bold">{item.title || "제목 없음"}</p>
            </a>
            <div className="h-[19px] overflow-ellipsis overflow-hidden relative shrink-0 text-[#65676e] text-base lg:text-lg text-nowrap tracking-[-0.32px] w-full mt-1 lg:mt-2">
              <p className="[text-overflow:inherit] [text-wrap-mode:inherit] [white-space-collapse:inherit] leading-[normal] overflow-inherit">{item.createdAt ? new Date(item.createdAt).toLocaleDateString('ko-KR') : ''}</p>
            </div>
          </div>
        </div>
      </div>
      <BottomToast isVisible={toastVisible} onClose={() => setToastVisible(false)} message="전체 서비스를 이용하시려면 로그인 해주세요." variant="error" />
    </div>
  );
}

function TwoRowFourColumnReportList({ 
  selectedCategory,
  isInitialLoad,
  onInitialLoadComplete
}: { 
  selectedCategory: string;
  isInitialLoad: boolean;
  onInitialLoadComplete: () => void;
}) {
  const [items, setItems] = useState<ReportItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [slideDirection, setSlideDirection] = useState<'left' | 'right' | null>(null);
  const [screenSize, setScreenSize] = useState<'mobile' | 'desktop'>('desktop');

  // 화면 크기 감지 함수
  const handleResize = useCallback(() => {
    const width = window.innerWidth;
    if (width < 1024) { // lg breakpoint - 1024px 이하를 모두 모바일로 인식
      setScreenSize('mobile');
    } else {
      setScreenSize('desktop');
    }
  }, []);

  // 화면 크기 감지
  useEffect(() => {
    handleResize(); // 초기 설정
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);

  // 화면 크기에 따른 보여줄 카드 수 계산
  const getCardsToShow = () => {
    switch (screenSize) {
      case 'mobile': return 2; // 1줄 1개, 총 2줄
      case 'desktop': return 8; // 1줄 4개, 총 2줄
      default: return 8;
    }
  };

  // 화면 크기에 따른 그리드 클래스 계산
  const getGridClass = () => {
    switch (screenSize) {
      case 'mobile': return 'grid-cols-1'; // 1줄 1개
      case 'desktop': return 'grid-cols-4'; // 1줄 4개
      default: return 'grid-cols-4';
    }
  };

  useEffect(() => {
    if (!selectedCategory) return;
    
    setLoading(true);
    setIsTransitioning(!isInitialLoad);
    
    // 카테고리 변경 시 슬라이드 방향 설정 (간단하게 left로 고정)
    if (!isInitialLoad) {
      setSlideDirection('left');
    }
    
    // 카테고리가 'all'이거나 빈 문자열인 경우 전체 조회, 그 외에는 카테고리 필터링 적용
    const categoryParam = (selectedCategory === 'all' || selectedCategory === '') ? '' : selectedCategory;
    
    // 최대 8개까지 가져와서 화면 크기에 따라 필터링 (API 호출 최소화)
    const apiPromise = categoryParam
      ? reportService.search({ query: '', category: categoryParam, page: 1, size: 8 })
      : reportService.getLatest(1, 8);
    
    apiPromise
      .then((response) => {
        // To-Be 형식 처리
        let apiReports: ApiReportItem[] = [];
        if (response.code === "SUCCESS" && response.data) {
          if (Array.isArray(response.data)) {
            apiReports = response.data;
          } else if ('items' in response.data) {
            apiReports = response.data.items;
          }
        }
        
        // API 타입을 로컬 타입으로 변환 (reportId → id)
        const reports = apiReports.map(item => ({
          id: item.id,
          title: item.title,
          summary: item.summary,
          createdAt: item.createdAt,
          thumbnailUrl: item.thumbnailUrl,
          keywords: item.keywords
        }));
        
        setItems(reports);
      })
      .catch(() => setItems([]))
      .finally(() => { 
        setLoading(false);
        setIsTransitioning(false);
        setSlideDirection(null);
        if (isInitialLoad) {
          onInitialLoadComplete();
        }
      });
  }, [selectedCategory, isInitialLoad, onInitialLoadComplete]);

  if (isInitialLoad && loading) {
    return (
      <div className="content-stretch flex items-center justify-center relative w-full" style={{ height: 804 }}>
        <div className="size-10 animate-spin rounded-full border-4 border-[#dadbe0] border-t-[#43464d]" aria-label="로딩 중" />
      </div>
    );
  }

  if (!items.length && !loading) {
    return (
      <div className="content-stretch flex items-center justify-center relative w-full" style={{ height: 400 }}>
        <div className="text-gray-500 text-lg">해당 카테고리에 콘텐츠가 없습니다.</div>
      </div>
    );
  }

  return (
    <div className="relative w-full overflow-hidden">
      <AnimatePresence mode="wait">
        <motion.div
          key={`category-${selectedCategory}`}
          initial={{ x: 0, opacity: 1 }}
          animate={{ x: 0, opacity: 1 }}
          exit={slideDirection ? {
            x: slideDirection === 'left' ? '-100%' : '100%',
            opacity: 0.7
          } : { x: 0, opacity: 1 }}
          transition={{
            type: "spring",
            duration: 0.3,
            bounce: 0,
            damping: 30,
            stiffness: 400
          }}
          className={`${screenSize === 'mobile' ? 'gap-6 sm:gap-8' : 'gap-[30px]'} grid ${getGridClass()} auto-rows-auto h-auto overflow-visible relative shrink-0 w-full`}
        >
          {items.slice(0, getCardsToShow()).map((item, i) => (
            <motion.div 
              key={`item-${item.id || `unknown-${i}`}-category-${selectedCategory}`} 
              className="w-full flex flex-col gap-4 sm:gap-6 h-auto items-start justify-start relative shrink-0"
              initial={{ opacity: 0, y: 15, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{
                delay: isTransitioning ? i * 0.03 : i * 0.05,
                duration: isTransitioning ? 0.2 : 0.3,
                ease: [0.25, 0.1, 0.25, 1]
              }}
            >
              <SmallReportCard item={item as ReportItem} isMobile={screenSize === 'mobile'} />
            </motion.div>
          ))}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

export function TwoRowFourColumnReportSection({ 
  blueTitle, 
  blackTitle, 
  titleHtml 
}: { 
  blueTitle: string; 
  blackTitle: string; 
  titleHtml?: string; 
}) {
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [categories, setCategories] = useState<Array<{ code: string; name: string }>>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [screenSize, setScreenSize] = useState<'mobile' | 'desktop'>('desktop');
  
  // 터치 스와이프 상태
  const [touchStart, setTouchStart] = useState<{ x: number; y: number } | null>(null);

  // 화면 크기 감지
  const handleResize = useCallback(() => {
    const width = window.innerWidth;
    if (width < 1024) { // lg breakpoint - 1024px 이하를 모두 모바일로 인식
      setScreenSize('mobile');
    } else {
      setScreenSize('desktop');
    }
  }, []);

  // 화면 크기 감지 이벤트 리스너
  useEffect(() => {
    handleResize(); // 초기 설정
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);

  // 터치 이벤트 핸들러 - 매우 단순하게 구현
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (screenSize !== 'mobile' || categoriesLoading || categories.length <= 1) return;
    
    const touch = e.touches[0];
    setTouchStart({ x: touch.clientX, y: touch.clientY });
  }, [screenSize, categoriesLoading, categories.length]);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    if (!touchStart || categoriesLoading || categories.length <= 1) {
      setTouchStart(null);
      return;
    }
    
    const touch = e.changedTouches[0];
    const deltaX = touch.clientX - touchStart.x;
    const deltaY = touch.clientY - touchStart.y;
    const minSwipeDistance = 100; // 최소 스와이프 거리
    const maxVerticalDistance = 30; // 수직 스와이프 허용 범위
    
    // 수직 스와이프가 너무 크면 무시 (스크롤과 구분)
    if (Math.abs(deltaY) > maxVerticalDistance) {
      setTouchStart(null);
      return;
    }
    
    // 좌우 스와이프 감지 - 단순하게 다음/이전 카테고리만
    if (Math.abs(deltaX) > minSwipeDistance) {
      const currentIndex = categories.findIndex(cat => cat.code === selectedCategory);
      
      if (deltaX > 0) {
        // 오른쪽으로 스와이프 (이전 카테고리)
        if (currentIndex > 0) {
          setSelectedCategory(categories[currentIndex - 1].code);
        }
      } else {
        // 왼쪽으로 스와이프 (다음 카테고리)
        if (currentIndex < categories.length - 1) {
          setSelectedCategory(categories[currentIndex + 1].code);
        }
      }
    }
    
    setTouchStart(null);
  }, [touchStart, categoriesLoading, categories, selectedCategory]);

  // 카테고리 데이터 로딩
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const responseData = await reportService.getCategories();
        // To-Be 형식 처리
        const data = responseData.code === "SUCCESS" && responseData.data?.categories 
          ? responseData.data.categories 
          : [];
        // API 응답이 배열인 경우 그대로 사용, 객체인 경우 적절히 변환
        let categoryArray: Array<{ code: string; name: string }> = [];
        
        if (Array.isArray(data)) {
          categoryArray = data;
        } else if (data && typeof data === 'object') {
          // 객체인 경우 values()를 사용하거나 적절히 변환
          categoryArray = Object.values(data).map((item: any) => ({
            code: item.code || item.id || item.key || '',
            name: item.name || item.title || item.label || ''
          }));
        }
        
        // 중복 제거 및 기타 카테고리 제거
        const uniqueCategories = categoryArray.filter((item, index, self) => 
          index === self.findIndex(t => t.code === item.code)
        );
        
        // 기타 카테고리 제거
        const filteredCategories = uniqueCategories.filter(cat => 
          cat.code !== 'etc' && cat.name !== '기타'
        );
        
        // 전체 카테고리를 맨 앞으로 이동
        const allCategory = filteredCategories.find(cat => cat.code === 'all' || cat.name === '전체');
        const otherCategories = filteredCategories.filter(cat => cat.code !== 'all' && cat.name !== '전체');
        
        let finalCategories;
        if (allCategory) {
          finalCategories = [allCategory, ...otherCategories];
        } else {
          finalCategories = filteredCategories;
        }
        
        setCategories(finalCategories);
        
        // 첫 번째 카테고리(전체)를 기본 선택으로 설정
        if (finalCategories.length > 0 && !selectedCategory) {
          setSelectedCategory(finalCategories[0].code);
        }
      } catch (error) {
        console.error('카테고리 로딩 실패:', error);
        // 에러 시 기본 카테고리 사용
        const fallbackCategories = [
          { code: 'all', name: '전체' },
          // { code: 'commerce', name: '커머스' },
          // { code: 'marketing', name: '마케팅' },
          // { code: 'design', name: '디자인' },
          // { code: 'life', name: '라이프' }
        ];
        setCategories(fallbackCategories);
        
        // 첫 번째 카테고리(전체)를 기본 선택으로 설정
        if (!selectedCategory) {
          setSelectedCategory(fallbackCategories[0].code);
        }
      } finally {
        setCategoriesLoading(false);
      }
    };

    fetchCategories();
  }, []);

  return (
    <div className="content-stretch flex flex-col gap-5 items-center justify-start relative size-full">
      {/* 타이틀과 카테고리 영역 - 항상 표시 */}
      <SectionTitle 
        blueText={blueTitle} 
        blackText={blackTitle} 
        titleHtml={titleHtml}
        viewAllHref="/reportlist" 
        viewAllPublicAccess 
      />
      <div className="w-full flex justify-start overflow-x-auto">
        <CategoryButtons 
          categories={categories}
          selectedCategory={selectedCategory}
          onCategoryChange={setSelectedCategory}
          loading={categoriesLoading}
        />
      </div>
      
      {/* 리스트 영역 - 로딩 상태에 따라 다르게 표시 */}
      <div 
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        style={{
          touchAction: screenSize === 'mobile' ? 'pan-y' : 'auto'
        }}
      >
        <TwoRowFourColumnReportList 
          selectedCategory={selectedCategory}
          isInitialLoad={isInitialLoad}
          onInitialLoadComplete={() => setIsInitialLoad(false)}
        />
      </div>
    </div>
  );
}

